import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Loader2, AlertCircle, Sparkles, TrendingUp, BarChart3 } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '../config/api';
import { useAuth } from '../context/AuthContext';

export default function PromotionSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState(sessionId ? 'checking' : 'error');
  const [error, setError] = useState(sessionId ? null : 'No session ID found');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    let count = 0;

    const poll = async () => {
      if (cancelled || count >= 10) {
        if (!cancelled && count >= 10) {
          setStatus('success'); // Assume success if we can't verify — Stripe will handle
        }
        return;
      }
      try {
        const res = await fetch(`${API}/promotions/status/${sessionId}`, {
          headers: getAuthHeaders(),
        });
        if (res.ok) {
          const data = await res.json();
          if (data.payment_status === 'paid' || data.status === 'active') {
            setStatus('success');
            return;
          }
        }
      } catch {}
      count++;
      setAttempts(count);
      if (!cancelled) setTimeout(poll, 2000);
    };

    // Give Stripe webhook a moment
    setTimeout(poll, 1500);
    return () => { cancelled = true; };
  }, [sessionId]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="promotion-checking">
        <Card className="p-8 max-w-md w-full text-center">
          <Loader2 className="w-16 h-16 mx-auto mb-4 text-secondary animate-spin" />
          <h1 className="text-2xl font-bold uppercase mb-2">Verifying Payment</h1>
          <p className="text-muted-foreground">Confirming your promotion purchase...</p>
          <p className="text-xs text-muted-foreground mt-3">Attempt {attempts + 1} of 10</p>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" data-testid="promotion-error">
        <Card className="p-8 max-w-md w-full text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-bold uppercase mb-2">Payment Issue</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/promote')} className="bg-secondary hover:bg-secondary/90">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4" data-testid="promotion-success">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-lg mx-auto"
      >
        <Card className="p-8 text-center">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.2 }}
          >
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-500" />
            </div>
          </motion.div>

          <Badge className="mb-4 bg-yellow-500/20 text-yellow-400">Promotion Active</Badge>

          <h1 className="text-3xl font-black uppercase mb-3">You're Live!</h1>
          <p className="text-muted-foreground mb-8">
            Your paid promotion is now active. Your content will be featured on the Soundstage homepage and charts.
          </p>

          <div className="space-y-3">
            <Button
              className="w-full bg-secondary hover:bg-secondary/90"
              onClick={() => navigate('/')}
              data-testid="go-home-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> View on Homepage
            </Button>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => navigate('/charts')}
              data-testid="go-charts-btn"
            >
              <BarChart3 className="w-4 h-4 mr-2" /> View Charts
            </Button>
            <Button
              variant="ghost"
              className="w-full"
              onClick={() => navigate('/promote')}
            >
              Promote More Content
            </Button>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}
